import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { SiteFooter } from "@/components/SiteFooter";
import { PageGallery } from "@/components/PageGallery";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";


import { TRANSLATION_FAQ_CATEGORIES } from "@/content/translation-corpus/faq-defs";
import { getActiveProvince } from "@/provinces/_registry";

export const Route = createFileRoute("/translation/faq/")({
  loader: async (): Promise<{ TRANSLATION_FAQ_TH: { q: string; a: string }[]; TRANSLATION_KEYWORDS_TH: string[]; TRANSLATION_QUESTIONS_TH: string[] }> => {
    const [m0, m1] = await Promise.all([
      import("@/content/translation-corpus/faq-th"),
      import("@/content/translation-corpus"),
    ]);
    return { TRANSLATION_FAQ_TH: m0.TRANSLATION_FAQ_TH, TRANSLATION_KEYWORDS_TH: m1.TRANSLATION_KEYWORDS_TH, TRANSLATION_QUESTIONS_TH: m1.TRANSLATION_QUESTIONS_TH };
  },
  component: TranslationFaqHub,
  head: ({ loaderData }) => {
    const { TRANSLATION_FAQ_TH = [], TRANSLATION_KEYWORDS_TH = [], TRANSLATION_QUESTIONS_TH = [] } = loaderData ?? {};

    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    return {
      ...pageHeadForProvince({
        title: `แปลและรับรองเอกสารทุกภาษา FAQ 2026 — ค่าแปล ระยะเวลา นิติกรณ์ | ${config.nameTh}`,
        description:
          "คำถามที่พบบ่อยเรื่องแปลเอกสารและรับรองคำแปลเพื่อใช้ในประเทศไทย — 28 ภาษา ค่าธรรมเนียม 200/400 บาท ยื่นอำเภอ ตม. กรมที่ดิน DBD และ Apostille 2026 ตอบโดยทีมทนาย",
        path: "/translation/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/translation/faq" }],
        keywords: TRANSLATION_KEYWORDS_TH.slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "แปลเอกสาร FAQ", url: `${site}/translation/faq` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(TRANSLATION_FAQ_TH)) },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
            keywords: TRANSLATION_KEYWORDS_TH.slice(0, 100).join(", "),
          }),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/translation/faq")) },
      ],
    };
  },
});

function TranslationFaqHub() {
  const { TRANSLATION_FAQ_TH, TRANSLATION_KEYWORDS_TH, TRANSLATION_QUESTIONS_TH } = Route.useLoaderData() as { TRANSLATION_FAQ_TH: { q: string; a: string }[]; TRANSLATION_KEYWORDS_TH: string[]; TRANSLATION_QUESTIONS_TH: string[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/translation" className="hover:underline">แปลเอกสาร</a> <span>/</span> <span>FAQ</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          แปลและรับรองเอกสารทุกภาษา — FAQ {TRANSLATION_FAQ_TH.length} ข้อ (2026)
        </h1>
        <p className="text-muted-foreground mb-8">
          คำตอบฉบับทนายเรื่องการแปลเอกสารเพื่อใช้ในประเทศไทย — ระดับการรับรอง ค่าแปล
          ระยะเวลา การสะกดชื่อตามพาสปอร์ต การยื่นอำเภอ ตม. กรมที่ดิน DBD และ Apostille ปี 2026
        </p>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/translation/faq" className="text-primary underline">
            Certified translation FAQ for expats →
          </a>
        </div>

        <section aria-label="หมวดคำถามงานแปล" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">หมวดคำถาม</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {TRANSLATION_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/translation/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="font-semibold mb-1">{c.titleTh} →</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {TRANSLATION_FAQ_TH.map((item, i) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">ให้เราแปลและเดินเรื่องรับรองให้ครบวงจร</h2>
          <p className="text-sm text-muted-foreground mb-4">
            รับเอกสาร → แปล 28 ภาษา → รับรองทนาย/นิติกรณ์ MFA → สถานทูตปลายทาง → ส่งคืน EMS/DHL
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            ขอใบเสนอราคา →
          </a>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">คำถามที่ผู้ใช้ค้นหาบ่อย ({TRANSLATION_QUESTIONS_TH.length} คำถาม)</h2>
          <ul className="grid gap-1 text-sm text-muted-foreground md:grid-cols-2">
            {TRANSLATION_QUESTIONS_TH.slice(0, 120).map((q, i) => (
              <li key={i}>• {q}</li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">หัวข้อยอดฮิต ({TRANSLATION_KEYWORDS_TH.length} คำค้น)</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {TRANSLATION_KEYWORDS_TH.slice(0, 300).map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">{kw}</span>
            ))}
          </div>
        </section>
      
        <FaqSourceNote topic="translation" locale="th" />
        <div className="mb-10">
          <PageGallery category="translation" seed="translation-faq.index" locale="th" />
        </div>

      </main>
      <SiteFooter />
    </>
  );
}
